import React from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import { LineChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { getColor, rgbaColor } from "helpers/utils";

echarts.use([LineChart, TooltipComponent, GridComponent, LegendComponent]);

const BarGraph = ({ financeData, graphKey, countKey, style }) => {
  const hours = Array.from({ length: 24 }, (_, index) => index);
  const days = hours.map(
    (hour) => `${hour % 12 === 0 ? 12 : hour % 12} ${hour < 12 ? "am" : "pm"}`
  );

  const extractTotalSum = (data, target, graphKey, countKey) => {
    const totalSums = {
      web_shopify: [],
      web_native: [],
      crm: [],
      app_shopflo: [],
      app_juspay: [],
    };

    const totalReturnSums = [];
    const totalReturnCounts = [];

    for (const day in data[target]) {
      const dayData = data[target][day];
      let sumData = 0;
      let sumCount = 0;
      for (const category in totalSums) {
        sumData += dayData[category] ? dayData[category][graphKey] || 0 : 0;
        sumCount += dayData[category] ? dayData[category][countKey] || 0 : 0;
      }
      let fixedData = sumData.toFixed(2) || 0;
      totalReturnSums.push(fixedData);
      totalReturnCounts.push(sumCount);
    }
    return { sums: totalReturnSums, counts: totalReturnCounts };
  };

  const { sums: totalTodaySums, counts: totalTodayCounts } = extractTotalSum(
    financeData,
    "today",
    graphKey,
    countKey
  );
  const { sums: totalYesterdaySums, counts: totalYesterdayCounts } =
    extractTotalSum(financeData, "yesterday", graphKey, countKey);
  const getOption = (data1, data2, counts1, counts2) => ({
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "none",
      },
      padding: [7, 10],
      backgroundColor: getColor("gray-100"),
      borderColor: getColor("gray-300"),
      borderWidth: 1,
      transitionDuration: 0,
      textStyle: {
        fontWeight: 500,
        fontSize: 12,
        color: getColor("dark"),
      },
      formatter: (params) => {
        const todayValue = data1[params[0].dataIndex];
        const yesterdayValue = data2[params[0].dataIndex];
        const todayCount = counts1[params[0].dataIndex];
        const yesterdayCount = counts2[params[0].dataIndex];
        return `<span style="margin-left: 20px;"> ${params[0].axisValue} </span> <br>
        <span style="color:${getColor('primary')}; margin-left: 20px;">Today:</span> ${todayValue} INR, Count: ${todayCount}<br>
        <span style="color:${getColor('warning')}">Yesterday:</span> ${yesterdayValue} INR, Count: ${yesterdayCount}`;     
      },
    },
    xAxis: {
      type: "category",
      data: [
        "12 AM",
        "1 AM",
        "2 AM",
        "3 AM",
        "4 AM",
        "5 AM",
        "6 AM",
        "7 AM",
        "8 AM",
        "9 AM",
        "10 AM",
        "11 AM",
        "12 PM",
        "1 PM",
        "2 PM",
        "3 PM",
        "4 PM",
        "5 PM",
        "6 PM",
        "7 PM",
        "8 PM",
        "9 PM",
        "10 PM",
        "11 PM",
      ],
      boundaryGap: false,
      splitLine: {
        show: true,
        lineStyle: {
          color: rgbaColor("#fff", 0.1),
        },
        interval: 0,
      },
      axisLine: {
        lineStyle: {
          color: rgbaColor("#fff", 0.1),
        },
      },
      axisTick: {
        show: true,
        length: 10,
        lineStyle: {
          color: rgbaColor("#fff", 0.1),
        },
      },
      axisLabel: {
        color: getColor("white"),
        fontWeight: 600,
        fontSize: 12,
        margin: 15,
        interval: window.innerWidth < 768 ? "auto" : 0,
        formatter: (value) => value.substring(0, value.length - 3),
      },
    },
    yAxis: {
      type: "value",
      axisPointer: {
        show: false,
      },
      splitLine: {
        show: false,
      },
      axisLabel: {
        show: false,
      },
      axisTick: { show: false },
      axisLine: { show: false },
    },

    series: [
      {
        type: "line",
        smooth: true,
        data: data1,
        symbol: "emptyCircle",
        itemStyle: {
          color: getColor("white"),
        },
        lineStyle: {
          color: rgbaColor(getColor("white"), 0.8),
        },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: rgbaColor("#fff", 0.5),
              },
              {
                offset: 1,
                color:rgbaColor("#fff", 0),
              },
            ],
          },
        },
        emphasis: {
          lineStyle: {
            width: 2,
          },
        },
      },
      {
        type: "line",
        smooth: true,
        data: data2,
        symbol: "emptyCircle",
        itemStyle: {
          color: getColor("white"),
        },
        lineStyle: {
          color: rgbaColor(getColor("white"), 0.5),
          type: "dotted", 
        },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: rgbaColor("#fff", 0.5),
              },
              {
                offset: 1,
                color:  rgbaColor("#fff", 0),
              },
            ],
          },
        },
        emphasis: {
          lineStyle: {
            width: 2,
          },
        },
      },
    ],
    grid: { right: 15, left: 15, bottom: "15%", top: 0 },
  });

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getOption(
        totalTodaySums,
        totalYesterdaySums,
        totalTodayCounts,
        totalYesterdayCounts
      )}
      style={{height:"25rem"}}
      className="bg-primary rounded-4"
      
    />
  );
};

export default BarGraph;
